import {
    emptyContenedoresResponse,
    getIndexMaterialHijoEnDescarga,
    getIndexMaterialPadreEnDescarga,
    getMaterialPadreEnDescarga,
} from "./common";
import {
    dataTableDescarga,
    dataTableDetalle,
    freezeAllDataTables,
    mostrarDetallePedimentoSeleccionado,
} from "./dataTables";
import { alterFinalizarDescarga } from "./descarga.alerts";
import { finalizaDescarga } from "./descarga.finalizar";
import { DESCARGO_POR_LISTA_MATERIALES, DESCARGO_UNO_A_UNO, DescargaCommonGlobal } from "./descarga.global";
import { getContenedoresImportacion, getListaMateriales, verificaSinFechaVencimiento } from "./utils";
import { btnCancelaDescargo, btnDescarga, btnSiguienteMaterial } from "./view";

/**
 * Inicializa el recorrido por lista de materiales
 * @returns
 */
const initRecorridoPorListaMateriales = async () => {
    const materialPadreEnDescarga = getMaterialPadreEnDescarga();

    const pedimentoExportacionData =
        DescargaCommonGlobal.pedimentosExportacionValidos[DescargaCommonGlobal.enDescarga.pedimentoId];

    const listaMateriales = await getListaMateriales(materialPadreEnDescarga.material, pedimentoExportacionData.fecha);

    if (!listaMateriales) {
        return emptyContenedoresResponse;
    }

    const { cantidadPorDescargar } = materialPadreEnDescarga;
    listaMateriales.detalles = listaMateriales.detalles.map((detalle) => ({
        ...detalle,
        cantidadPorDescargar: cantidadPorDescargar * detalle.cantidad_utilizada,
        uuid: uuidv4(),
    }));

    materialPadreEnDescarga.listaMateriales = listaMateriales;

    const { detalles } = listaMateriales;
    const primerDetalle = detalles[0];
    DescargaCommonGlobal.enDescarga.hijoId = primerDetalle.uuid;

    const { simulacion_descarga, sin_fecha_vencimiento } = await getContenedoresImportacion([
        primerDetalle.material_hijo,
    ]);

    return {
        simulacionDescarga: simulacion_descarga,
        sinFechaVencimiento: sin_fecha_vencimiento,
    };
};

/**
 * Obtiene el siguiente material padre para recorrer
 * @returns
 */
const getSiguienteMaterialPadre = (materialPadreCurrentIndex) => {
    let siguienteMaterialIndex = null;

    if (materialPadreCurrentIndex === undefined) {
        const materialPadreIndex = getIndexMaterialPadreEnDescarga();
        siguienteMaterialIndex = materialPadreIndex + 1;
    } else {
        siguienteMaterialIndex = materialPadreCurrentIndex + 1;
    }

    if (DescargaCommonGlobal.enDescarga.materialesPadre.length <= siguienteMaterialIndex) {
        return null;
    }

    return DescargaCommonGlobal.enDescarga.materialesPadre[siguienteMaterialIndex];
};

/**
 * Obtiene el siguiente material hijo para recorrer, basado en la lista
 * de materiales del material padre
 * @returns
 */
const getSiguienteMaterialHijo = () => {
    const materialPadre = getMaterialPadreEnDescarga();

    if (!materialPadre.listaMateriales) return null;

    const materialHijoIndex = getIndexMaterialHijoEnDescarga(materialPadre);

    const siguienteMaterialIndex = materialHijoIndex + 1;
    if (materialPadre.listaMateriales.detalles.length <= siguienteMaterialIndex) {
        return null;
    }

    return materialPadre.listaMateriales.detalles[siguienteMaterialIndex];
};

/**
 * Genera los nuevos materiales padre a recorrer para los hijos con cantidad pendiente
 * por descargar
 * @returns
 */
const getMaterialesPadreConCantidadPorDescargar = () => {
    const { materialesPadre } = DescargaCommonGlobal.enDescarga;

    const nuevosMaterialesPadre = materialesPadre.reduce((previosValue, currentValue) => {
        const { listaMateriales, pedimento_id, contenedor_id, clave_documento } = currentValue;

        if (listaMateriales) {
            const { detalles } = listaMateriales;

            const detallesToMaterialPadre = detalles
                .filter((itm) => itm.cantidadPorDescargar > 0)
                .map((itm) => ({
                    id: itm.id,
                    material: itm.material_hijo,
                    cantidad: itm.cantidadPorDescargar,
                    cantidadPorDescargar: itm.cantidadPorDescargar,
                    fecha: currentValue.fecha,
                    pedimento_id,
                    contenedor_id,
                    clave_documento,
                    uuid: uuidv4(),
                }));

            if (detallesToMaterialPadre.length > 0) {
                previosValue.push(...detallesToMaterialPadre);
            }
        }

        return previosValue;
    }, []);

    DescargaCommonGlobal.enDescarga.materialesPadre = [];

    if (nuevosMaterialesPadre.length === 0) {
        return null;
    }

    return nuevosMaterialesPadre;
};

/**
 * Maneja el recorrido de los materiales, si se llega al final de un nivel,
 * construye el siguiente nivel en caso de existir
 */
export const getSiguienteMaterial = async () => {
    const siguienteMaterialHijo = getSiguienteMaterialHijo();

    if (siguienteMaterialHijo) {
        DescargaCommonGlobal.enDescarga.hijoId = siguienteMaterialHijo.uuid;

        const { simulacion_descarga, sin_fecha_vencimiento } = await getContenedoresImportacion([
            siguienteMaterialHijo.material_hijo,
        ]);

        return {
            simulacionDescarga: simulacion_descarga,
            sinFechaVencimiento: sin_fecha_vencimiento,
            isEnd: false,
        };
    }

    const materialPadreCurrentIndex = getIndexMaterialPadreEnDescarga();

    DescargaCommonGlobal.enDescarga.hijoId = null;
    DescargaCommonGlobal.enDescarga.padreId = null;

    let siguienteMaterialPadre = getSiguienteMaterialPadre(materialPadreCurrentIndex);

    if (!siguienteMaterialPadre) {
        const nuevosMaterialPadre = getMaterialesPadreConCantidadPorDescargar();

        if (!nuevosMaterialPadre) {
            return {
                ...emptyContenedoresResponse,
                isEnd: true,
            };
        }

        DescargaCommonGlobal.enDescarga.materialesPadre = nuevosMaterialPadre;
        siguienteMaterialPadre = nuevosMaterialPadre[0];
    }

    DescargaCommonGlobal.enDescarga.padreId = siguienteMaterialPadre.uuid;

    const respuestaListaMateriales = await initRecorridoPorListaMateriales();
    return {
        ...respuestaListaMateriales,
        isEnd: false,
    };
};

/**
 * Obitiene los contenedores de exportacion con cantidad pendiente por descargar
 * @returns
 */
const getContenedoresPorDescargar = () => {
    const { materialesPadre } = DescargaCommonGlobal.enDescarga;

    const nuevosMaterialesPadre = materialesPadre.filter((itm) => itm.cantidadPorDescargar > 0);

    return nuevosMaterialesPadre;
};

/**
 * Maneja el evento onclick del boton "Siguiente material"
 * @returns
 */
export const handleSiguienteMaterial = async () => {
    freezeAllDataTables();

    btnDescarga.disabled = true;
    btnSiguienteMaterial.disabled = true;
    btnCancelaDescargo.disabled = true;

    DescargaCommonGlobal.contenedoresImportacionParaDescargar = [];

    dataTableDescarga.options.data = [];
    dataTableDescarga.refresh();

    dataTableDetalle.options.data = [];
    dataTableDetalle.refresh();

    if (DescargaCommonGlobal.enDescarga.tipo === DESCARGO_UNO_A_UNO) {
        const siguienteMaterialPadre = getSiguienteMaterialPadre();

        if (siguienteMaterialPadre) {
            DescargaCommonGlobal.enDescarga.padreId = siguienteMaterialPadre.uuid;
            const respuestaContenedoresImportacion = await getContenedoresImportacion([
                siguienteMaterialPadre.material,
            ]);

            DescargaCommonGlobal.contenedoresImportacionParaDescargar = [
                ...respuestaContenedoresImportacion.simulacion_descarga,
            ];

            verificaSinFechaVencimiento(respuestaContenedoresImportacion.sin_fecha_vencimiento);
        } else {
            DescargaCommonGlobal.enDescarga.tipo = DESCARGO_POR_LISTA_MATERIALES;

            const contenedoresPorDescargar = getContenedoresPorDescargar();

            if (contenedoresPorDescargar.length === 0) {
                alterFinalizarDescarga().then(finalizaDescarga);
                return;
            }

            DescargaCommonGlobal.enDescarga.materialesPadre = contenedoresPorDescargar;

            const materialPadreADescargar = DescargaCommonGlobal.enDescarga.materialesPadre[0];
            DescargaCommonGlobal.enDescarga.padreId = materialPadreADescargar.uuid;

            const respuestaListaMateriales = await initRecorridoPorListaMateriales();

            DescargaCommonGlobal.contenedoresImportacionParaDescargar = [
                ...respuestaListaMateriales.simulacionDescarga,
            ];

            verificaSinFechaVencimiento(respuestaListaMateriales.sinFechaVencimiento);
        }
    } else {
        const respuestaListaMateriales = await getSiguienteMaterial();

        if (respuestaListaMateriales.isEnd) {
            alterFinalizarDescarga().then(finalizaDescarga);
            return;
        }

        DescargaCommonGlobal.contenedoresImportacionParaDescargar = [...respuestaListaMateriales.simulacionDescarga];

        verificaSinFechaVencimiento(respuestaListaMateriales.sinFechaVencimiento);
    }

    mostrarDetallePedimentoSeleccionado(DescargaCommonGlobal.enDescarga.pedimentoId);

    btnDescarga.disabled = false;
    btnSiguienteMaterial.disabled = false;
    btnCancelaDescargo.disabled = false;

    freezeAllDataTables(false);
};
